import { useTranslation } from 'react-i18next';
import React, { useState, useCallback } from 'react';
import { RoutingControls, DEFAULT_ROUTING, type RoutingChoice } from '../Info/RoutingControls';
import {
  Play, Footprints, Car, Bike, Circle, Loader2, Compass, CircleDot, AlertTriangle, X, MapPin,
} from 'lucide-react';
import { DraggablePanel } from '../common/DraggablePanel';
import { getMapColors } from '../../themes/mapTheme';
import { useMapStore } from '../../store/map.store';
import { cn } from '../../utils/cn';
import type {
  CatchmentSelection, CatchmentTimes, CatchmentOptions,
  LayerControl, TrafficControl,
} from '../Info/FeatureInfoPanel';

interface AnalysisPanelProps {
  onClose: () => void;
  selectedPoint: [number, number] | null; // [lon, lat]
  onPointChange?: (lonLat: [number, number] | null) => void;
  onStartCatchment?: (
    selections: CatchmentSelection[],
    times: CatchmentTimes,
    options: CatchmentOptions,
  ) => Promise<void>;
  isLoading?: boolean;
  error?: string | null;
  onDismissError?: () => void;
}

/* Legacy defaults */
const DEFAULT_WALKIN = ['5', '10', '15', '', ''];
const DEFAULT_DRIVE  = ['5', '15', '30', '45', ''];
const DEFAULT_MOTOR  = ['5', '15', '30', '45', ''];
const DEFAULT_RING   = ['0.25', '0.75', '1', '', ''];

const Pill: React.FC<{ active: boolean; onClick: () => void; children: React.ReactNode }> = ({ active, onClick, children }) => (
  <button
    onClick={onClick}
    className={cn(
      'px-2 py-0.5 rounded text-[10px] font-medium transition-colors border',
      active
        ? 'bg-gn-blue-light border-gn-blue text-gn-blue'
        : 'bg-gn-secondary border-gn-light text-gn-secondary hover:text-gn-primary hover:border-gn',
    )}
  >
    {children}
  </button>
);

const TimeRow: React.FC<{
  icon: React.ReactNode; label: string; values: string[];
  onChange: (v: string[]) => void; unit: string;
}> = ({ icon, label, values, onChange, unit }) => (
  <div className="flex items-center gap-1.5">
    {icon}
    <span className="text-[10px] text-gn-primary w-10 shrink-0">{label}</span>
    {values.map((t, i) => (
      <input key={i} type="text" value={t}
        onChange={(e) => { const n = [...values]; n[i] = e.target.value; onChange(n); }}
        className="w-11 px-1 py-1 text-[11px] text-center text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
        placeholder="0" />
    ))}
    <span className="text-[9px] text-gn-secondary">{unit}</span>
  </div>
);

export const AnalysisPanel: React.FC<AnalysisPanelProps> = ({
  onClose,
  selectedPoint,
  onPointChange,
  onStartCatchment,
  isLoading = false,
  error = null,
  onDismissError,
}) => {
  const { t } = useTranslation();
  const currentTool = useMapStore((s) => s.currentTool);

  /* Coordinate inputs for manual entry */
  const [lat, setLat] = useState('');
  const [lon, setLon] = useState('');

  /* Analysis name — required */
  const [analysisName, setAnalysisName] = useState('');

  /* Travel mode toggles */
  const [walkEnabled, setWalkEnabled] = useState(true);
  const [driveEnabled, setDriveEnabled] = useState(true);
  const [motorEnabled, setMotorEnabled] = useState(false);
  const [ringEnabled, setRingEnabled] = useState(false);

  /* Time inputs — legacy defaults */
  const [walkinTimes, setWalkinTimes] = useState(DEFAULT_WALKIN);
  const [driveTimes, setDriveTimes]   = useState(DEFAULT_DRIVE);
  const [motorTimes, setMotorTimes]   = useState(DEFAULT_MOTOR);
  const [ringDists, setRingDists]     = useState(DEFAULT_RING);

  /* Layer & traffic control */
  const [layerControl, setLayerControl]     = useState<LayerControl>('nbHood');
  const [trafficControl, setTrafficControl] = useState<TrafficControl>('hvytrf');
  const [routing, setRouting] = useState<RoutingChoice>(DEFAULT_ROUTING);

  // Sync manual inputs when map point is selected
  React.useEffect(() => {
    if (selectedPoint) {
      setLon(selectedPoint[0].toFixed(6));
      setLat(selectedPoint[1].toFixed(6));
    } else {
      setLon('');
      setLat('');
    }
  }, [selectedPoint]);

  // Reset internal state when tool type changes (catchment ↔ ringBuffer)
  React.useEffect(() => {
    setAnalysisName('');
    setWalkEnabled(true);
    setDriveEnabled(true);
    setMotorEnabled(false);
    setRingEnabled(false);
    setWalkinTimes(DEFAULT_WALKIN);
    setDriveTimes(DEFAULT_DRIVE);
    setMotorTimes(DEFAULT_MOTOR);
    setRingDists(DEFAULT_RING);
    setLayerControl('nbHood');
    setTrafficControl('hvytrf');
    setRouting(DEFAULT_ROUTING);
  }, [currentTool]);

  /* Notify parent when manual coordinates change (on blur / Enter) */
  const syncPoint = useCallback(() => {
    if (lat && lon && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lon))) {
      onPointChange?.([parseFloat(lon), parseFloat(lat)]);
    }
  }, [lat, lon, onPointChange]);

  const isRingBuffer = currentTool === 'ringBuffer';
  const isCatchment = currentTool === 'catchment';

  const title = isCatchment ? t('analysis.catchment')
    : isRingBuffer ? t('analysis.ring_buffer_analysis')
    : t('analysis.generic');

  const icon = isCatchment ? <Compass size={14} />
    : <CircleDot size={14} />;

  const hasPoint = lat && lon && !isNaN(parseFloat(lat)) && !isNaN(parseFloat(lon));
  const hasName = analysisName.trim().length > 0;
  const canStart = hasPoint && hasName && !isLoading;

  const handleStart = useCallback(() => {
    if (!canStart) return;

    /* When standalone ring buffer tool is active, only send ring traveller */
    const selections: CatchmentSelection[] = [{
      name: analysisName.trim(),
      lat: parseFloat(lat),
      lon: parseFloat(lon),
      walkin: isRingBuffer ? false : walkEnabled,
      drive:  isRingBuffer ? false : driveEnabled,
      motor:  isRingBuffer ? false : motorEnabled,
      ringBuffer: isRingBuffer ? true : ringEnabled,
    }];

    const times: CatchmentTimes = {
      walkin: walkinTimes.map(Number),
      drive:  driveTimes.map(Number),
      motor:  motorTimes.map(Number),
      ring:   ringDists.map(Number),
    };
    const options: CatchmentOptions = { layerControl, trafficControl, routing };

    onStartCatchment?.(selections, times, options);
  }, [canStart, analysisName, lat, lon, walkEnabled, driveEnabled, motorEnabled, ringEnabled,
      walkinTimes, driveTimes, motorTimes, ringDists, layerControl, trafficControl, routing, isRingBuffer, onStartCatchment]);

  return (
    <>
    {/* ═══ Loading overlay ═══ */}
    {isLoading && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 bg-gn-primary border border-gn rounded-xl px-8 py-6 shadow-2xl">
          <Loader2 size={36} className="text-gn-blue animate-spin" />
          <div className="text-center">
            <p className="text-gn-primary font-medium text-sm">{t('analysis.started', { title })}</p>
            <p className="text-gn-secondary text-[11px] mt-1">{t('analysis.analyzing')}</p>
          </div>
        </div>
      </div>
    )}

    {/* ═══ Error overlay ═══ */}
    {error && !isLoading && (
      <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/50 backdrop-blur-sm">
        <div className="flex flex-col items-center gap-4 bg-gn-primary border border-gn-danger rounded-xl px-8 py-6 shadow-2xl max-w-md">
          <AlertTriangle size={36} className="text-gn-danger" />
          <div className="text-center">
            <p className="text-gn-primary font-medium text-sm">{t('analysis.failed')}</p>
            <p className="text-gn-danger text-[11px] mt-1">{error}</p>
          </div>
          <button
            onClick={onDismissError}
            className="flex items-center gap-1.5 px-4 py-2 text-xs font-medium rounded bg-gn-tertiary text-gn-primary hover:bg-gn-hover transition-colors"
          >
            <X size={12} />
            {t('messages.close')}
          </button>
        </div>
      </div>
    )}

    <DraggablePanel title={title} onClose={onClose} icon={icon} width={360}>
      <div className="p-3 space-y-3">
        {/* Analysis Name — required */}
        <div className="space-y-1">
          <span className="text-[10px] text-gn-secondary font-medium">{t('analysis.name')} <span className="text-gn-danger">*</span></span>
          <input
            type="text"
            value={analysisName}
            onChange={(e) => setAnalysisName(e.target.value)}
            className="w-full px-2 py-1.5 text-[11px] text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
            placeholder={t('analysis.name_placeholder')}
          />
          {!hasName && (
            <span className="text-[9px] text-gn-warning">{t('analysis.name_required')}</span>
          )}
        </div>

        {/* Point coordinates */}
        <div className="space-y-1">
          <span className="text-[10px] text-gn-blue font-semibold flex items-center gap-1">
            <MapPin size={10} /> {t('analysis.point_hint')}
          </span>
          <div className="grid grid-cols-2 gap-2">
            <div>
              <label className="text-[9px] text-gn-secondary">{t('map.latitude')}</label>
              <input type="text" value={lat} onChange={(e) => setLat(e.target.value)}
                onBlur={syncPoint}
                onKeyDown={(e) => { if (e.key === 'Enter') syncPoint(); }}
                className="w-full px-2 py-1 text-[11px] text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
                placeholder={t('analysis.lat_example')} />
            </div>
            <div>
              <label className="text-[9px] text-gn-secondary">{t('map.longitude')}</label>
              <input type="text" value={lon} onChange={(e) => setLon(e.target.value)}
                onBlur={syncPoint}
                onKeyDown={(e) => { if (e.key === 'Enter') syncPoint(); }}
                className="w-full px-2 py-1 text-[11px] text-gn-primary bg-gn-tertiary border border-gn rounded focus:border-gn-blue focus:outline-none"
                placeholder={t('analysis.lon_example')} />
            </div>
          </div>
        </div>

        {/* Layer control */}
        <div className="space-y-1">
          <span className="text-[10px] text-gn-secondary font-medium">{t('analysis.layers')}</span>
          <div className="flex gap-1 flex-wrap">
            <Pill active={layerControl === 'nbHood'} onClick={() => setLayerControl('nbHood')}>{t('analysis.layer_nbhood')}</Pill>
            <Pill active={layerControl === 'allVisible'} onClick={() => setLayerControl('allVisible')}>{t('analysis.layer_all')}</Pill>
            <Pill active={layerControl === 'cosmos'} onClick={() => setLayerControl('cosmos')}>{t('analysis.layer_cosmos')}</Pill>
          </div>
        </div>

        {/* Traffic, direction and border */}
        {(driveEnabled || isCatchment) && (
          <div className="space-y-1">
            <RoutingControls traffic={trafficControl} onTraffic={setTrafficControl} routing={routing} onRouting={setRouting} />
          </div>
        )}

        {/* Travel modes + times */}
        {!isRingBuffer && (
          <div className="space-y-2">
            <span className="text-[10px] text-gn-secondary font-medium">{t('analysis.modes')}</span>

            {/* Walk-in */}
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-[11px] text-gn-primary">
                <input type="checkbox" checked={walkEnabled} onChange={(e) => setWalkEnabled(e.target.checked)}
                  className="rounded accent-blue-500" />
                <Footprints size={12} style={{ color: getMapColors().walk[0] }} /> {t('analysis.walking')}
              </label>
              {walkEnabled && (
                <TimeRow icon={<span />} label="" values={walkinTimes} onChange={setWalkinTimes} unit={t('analysis.unit_min')} />
              )}
            </div>

            {/* Drive */}
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-[11px] text-gn-primary">
                <input type="checkbox" checked={driveEnabled} onChange={(e) => setDriveEnabled(e.target.checked)}
                  className="rounded accent-blue-500" />
                <Car size={12} style={{ color: getMapColors().drive[0] }} /> {t('analysis.driving')}
              </label>
              {driveEnabled && (
                <TimeRow icon={<span />} label="" values={driveTimes} onChange={setDriveTimes} unit={t('analysis.unit_min')} />
              )}
            </div>

            {/* Motor */}
            <div className="space-y-1">
              <label className="flex items-center gap-2 text-[11px] text-gn-primary">
                <input type="checkbox" checked={motorEnabled} onChange={(e) => setMotorEnabled(e.target.checked)}
                  className="rounded accent-blue-500" />
                <Bike size={12} style={{ color: getMapColors().motor[0] }} /> {t('analysis.motorcycle')}
              </label>
              {motorEnabled && (
                <TimeRow icon={<span />} label="" values={motorTimes} onChange={setMotorTimes} unit={t('analysis.unit_min')} />
              )}
            </div>

            {/* Ring Buffer option within catchment */}
            {isCatchment && (
              <div className="space-y-1">
                <label className="flex items-center gap-2 text-[11px] text-gn-primary">
                  <input type="checkbox" checked={ringEnabled} onChange={(e) => setRingEnabled(e.target.checked)}
                    className="rounded accent-blue-500" />
                  <Circle size={12} style={{ color: getMapColors().ringBuffer }} /> {t('analysis.ring_buffer')}
                </label>
                {ringEnabled && (
                  <TimeRow icon={<span />} label="" values={ringDists} onChange={setRingDists} unit={t('analysis.unit_km')} />
                )}
              </div>
            )}
          </div>
        )}

        {/* Ring Buffer distances (standalone ring buffer tool) */}
        {isRingBuffer && (
          <div className="space-y-1">
            <span className="text-[10px] text-gn-secondary font-medium">{t('analysis.ring_distances')}</span>
            <TimeRow icon={<Circle size={12} style={{ color: getMapColors().ringBuffer }} />} label={t('analysis.ring')} values={ringDists} onChange={setRingDists} unit={t('analysis.unit_km')} />
          </div>
        )}

        {/* Start button */}
        <button
          onClick={handleStart}
          disabled={!canStart}
          className={cn(
            'w-full flex items-center justify-center gap-2 px-3 py-2.5 rounded text-xs font-medium transition-colors',
            !canStart
              ? 'bg-gn-tertiary text-gn-secondary cursor-not-allowed'
              : 'bg-gn-blue text-gn-inverse hover:bg-gn-blue',
          )}
        >
          {isLoading ? <Loader2 size={14} className="animate-spin" /> : <Play size={14} />}
          {t('analysis.start', { title })}
        </button>
      </div>
    </DraggablePanel>
    </>
  );
};
